/**
 * @fileoverview Bill Status Badge Component
 * @task B.INV.2 - Accounts Payable UI components
 *
 * Displays bill status with appropriate color coding:
 * - Draft: Gray
 * - Pending: Yellow
 * - Approved: Blue
 * - Paid: Green
 * - Overdue: Red
 */

import { cn } from '@lib/utils';
import type { BillStatus } from '@lib/demo-data';

interface BillStatusBadgeProps {
  status: BillStatus;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  isBrazil?: boolean;
}

const statusConfigEN: Record<
  BillStatus,
  { label: string; className: string; icon: JSX.Element }
> = {
  draft: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  pending: {
    label: 'Pending',
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    icon: (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  approved: {
    label: 'Approved',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  paid: {
    label: 'Paid',
    className: 'bg-green-50 text-green-700 border-green-200',
    icon: (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  overdue: {
    label: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: (
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
  },
};

const statusConfigBR: Record<
  BillStatus,
  { label: string; className: string; icon: JSX.Element }
> = {
  draft: {
    label: 'Rascunho',
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: statusConfigEN.draft.icon,
  },
  pending: {
    label: 'Pendente',
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    icon: statusConfigEN.pending.icon,
  },
  approved: {
    label: 'Aprovado',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: statusConfigEN.approved.icon,
  },
  paid: {
    label: 'Pago',
    className: 'bg-green-50 text-green-700 border-green-200',
    icon: statusConfigEN.paid.icon,
  },
  overdue: {
    label: 'Vencido',
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: statusConfigEN.overdue.icon,
  },
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
  lg: 'px-3 py-1.5 text-sm gap-2',
};

/**
 * Bill Status Badge
 * Displays a colored badge indicating the current status of a bill
 */
export function BillStatusBadge({
  status,
  className,
  size = 'md',
  isBrazil = false,
}: BillStatusBadgeProps): JSX.Element {
  const statusConfig = isBrazil ? statusConfigBR : statusConfigEN;
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses[size],
        config.className,
        className
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

export default BillStatusBadge;
